import { useState, useMemo } from 'react';
import {
  X,
  MessageSquare,
  Mail,
  PhoneCall,
  Send,
  Loader2,
  ExternalLink,
  Check,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import { Button } from '@lms/ui';
import {
  useSendQuickWhatsApp,
  useSendReminderEmail,
  useCreateOutreachLog,
} from '../../hooks/useCrm';
import { useLanguage } from '../../lib/LanguageContext';
import { copy } from '../../copy/copy';
import { Bilingual } from '../Bilingual';
import type {
  CrmParticipantItem,
  OutreachType,
  OutreachChannel,
  OutreachStatus,
} from '@lms/shared/types';

interface OutreachModalProps {
  isOpen: boolean;
  onClose: () => void;
  participant: CrmParticipantItem;
  initialTab?: 'WHATSAPP' | 'EMAIL' | 'MANUAL';
  initialType?: OutreachType;
}

export function OutreachModal({
  isOpen,
  onClose,
  participant,
  initialTab = 'WHATSAPP',
  initialType = 'INACTIVITY_REMINDER',
}: OutreachModalProps) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'WHATSAPP' | 'EMAIL' | 'MANUAL'>(initialTab);
  const [outreachType, setOutreachType] = useState<OutreachType>(initialType);
  const [selectedProgramId, setSelectedProgramId] = useState<string>(
    participant.enrolledPrograms[0]?.programId || ''
  );

  // WhatsApp Tab State. A null override means use the current template.
  const [customWaMessage, setCustomWaMessage] = useState<string | null>(null);
  const [waSuccessNotice, setWaSuccessNotice] = useState(false);

  // Email Tab State
  const [selectedEvaluationId, setSelectedEvaluationId] = useState<string>(
    participant.missedEvaluations[0]?.id || ''
  );
  const [emailSentSuccess, setEmailSentSuccess] = useState(false);

  // Manual Log Tab State
  const [manualChannel, setManualChannel] = useState<OutreachChannel>('PHONE');
  const [manualStatus, setManualStatus] = useState<OutreachStatus>('SENT');
  const [manualNotes, setManualNotes] = useState('');
  const [manualSuccess, setManualSuccess] = useState(false);

  const { mutate: sendWa, isPending: isSendingWa } = useSendQuickWhatsApp();
  const { mutate: sendEmail, isPending: isSendingEmail } = useSendReminderEmail();
  const { mutate: createLog, isPending: isCreatingLog } = useCreateOutreachLog();

  const selectedProgram = useMemo(() => {
    return participant.enrolledPrograms.find(p => p.programId === selectedProgramId);
  }, [participant.enrolledPrograms, selectedProgramId]);

  const defaultWaMessage = useMemo(() => {
    const sapaan = participant.gender === 'FEMALE' ? 'Ukhti' : 'Akhi';
    const progName = selectedProgram?.name
      ? `program ${selectedProgram.name}`
      : 'pembelajaran di TSL';

    if (outreachType === 'INACTIVITY_REMINDER') {
      return `Assalamu'alaikum Warahmatullahi Wabarakatuh, ${sapaan} ${participant.name}.\n\nKami dari Tim Layanan & Akademik Tarbiyah Sunnah Learning (TSL) ingin menyapa dan menanyakan kabar antum/antunna. Berdasarkan catatan sistem kami, sudah beberapa hari ini belum ada aktivitas login di ${progName}.\n\nApakah ada kendala teknis atau materi yang bisa kami bantu? Silakan akses kembali portal pembelajaran di: https://my.tsl.my.id\n\nSemoga Allah memudahkan langkah kita dalam menuntut ilmu syar'i. Baarakallahu fiikum.`;
    } else if (outreachType === 'EXAM_REMINDER') {
      return `Assalamu'alaikum Warahmatullahi Wabarakatuh, ${sapaan} ${participant.name}.\n\nKami dari Tim Akademik Tarbiyah Sunnah Learning (TSL) menginfokan bahwa terdapat evaluasi/ujian di ${progName} yang belum diselesaikan.\n\nMohon dapat meluangkan waktu untuk menuntaskan evaluasi tersebut melalui portal: https://my.tsl.my.id\n\nBaarakallahu fiikum.`;
    }
    return `Assalamu'alaikum Warahmatullahi Wabarakatuh, ${sapaan} ${participant.name}.\n\nSalam hangat dari Tim Akademik Tarbiyah Sunnah Learning (TSL). Kami ingin menyapa dan menanyakan progres pembelajaran antum di ${progName}. Jika ada kendala, jangan ragu untuk mengabari kami.\n\nBaarakallahu fiikum.`;
  }, [outreachType, selectedProgram, participant]);
  const waMessage = customWaMessage ?? defaultWaMessage;

  if (!isOpen) return null;

  const handleSendWhatsApp = () => {
    if (!participant.phone) return;
    sendWa(
      {
        userId: participant.id,
        programId: selectedProgramId || undefined,
        type: outreachType,
        customMessage: waMessage,
      },
      {
        onSuccess: res => {
          setWaSuccessNotice(true);
          // Open WhatsApp web/app in a new tab
          window.open(res.waUrl, '_blank', 'noopener,noreferrer');
          setTimeout(() => {
            onClose();
          }, 1500);
        },
      }
    );
  };

  const handleSendEmail = () => {
    sendEmail(
      {
        userId: participant.id,
        programId: selectedProgramId || undefined,
        type: outreachType,
        evaluationId: selectedEvaluationId || undefined,
      },
      {
        onSuccess: () => {
          setEmailSentSuccess(true);
          setTimeout(() => {
            onClose();
          }, 1500);
        },
      }
    );
  };

  const handleSaveManualLog = (e: React.FormEvent) => {
    e.preventDefault();
    createLog(
      {
        userId: participant.id,
        programId: selectedProgramId || undefined,
        channel: manualChannel,
        type: outreachType,
        status: manualStatus,
        notes: manualNotes,
      },
      {
        onSuccess: () => {
          setManualSuccess(true);
          setTimeout(() => {
            onClose();
          }, 1200);
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-on-surface/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-fadeIn overflow-y-auto py-10">
      <div className="bg-surface-container-lowest rounded-[2rem] w-full max-w-2xl shadow-level4 overflow-hidden animate-slideUp border border-on-surface/5 my-auto">
        {/* Header */}
        <div className="p-6 sm:p-8 border-b border-outline-variant/10 bg-surface-container-low/40 flex justify-between items-start">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-[10px] font-headline font-bold uppercase tracking-wider mb-1">
              <Sparkles size={12} />
              <span>CRM & Engagement</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-headline font-bold text-on-surface m-0 tracking-tight">
              <Bilingual {...copy.crm.modalOutreachTitle} />
            </h2>
            <div className="flex items-center gap-2 text-xs font-body text-on-surface-variant pt-1">
              <span className="font-bold text-on-surface">{participant.name}</span>
              <span>•</span>
              <span className="font-mono text-primary">{participant.nip || participant.email}</span>
              {participant.phone && (
                <>
                  <span>•</span>
                  <span className="font-mono">{participant.phone}</span>
                </>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-on-surface/10 rounded-full transition-colors text-on-surface-variant"
            aria-label="Tutup modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Channel Switcher Tabs */}
        <div className="flex border-b border-outline-variant/10 bg-surface-container-low/20 px-6 pt-3 gap-2">
          <button
            type="button"
            onClick={() => setActiveTab('WHATSAPP')}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-headline font-bold border-b-2 transition-all cursor-pointer ${
              activeTab === 'WHATSAPP'
                ? 'border-emerald-600 text-emerald-600 bg-emerald-50/50 dark:bg-emerald-950/20 rounded-t-xl'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <MessageSquare size={16} />
            <Bilingual {...copy.crm.tabWhatsApp} />
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('EMAIL')}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-headline font-bold border-b-2 transition-all cursor-pointer ${
              activeTab === 'EMAIL'
                ? 'border-sky-600 text-sky-600 bg-sky-50/50 dark:bg-sky-950/20 rounded-t-xl'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <Mail size={16} />
            <Bilingual {...copy.crm.tabEmail} />
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('MANUAL')}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-headline font-bold border-b-2 transition-all cursor-pointer ${
              activeTab === 'MANUAL'
                ? 'border-primary text-primary bg-primary/5 rounded-t-xl'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <PhoneCall size={16} />
            <Bilingual {...copy.crm.tabManualLog} />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 sm:p-8 space-y-6">
          {/* Global Controls: Program Target & Outreach Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-1">
                Program Terkait
              </label>
              <select
                value={selectedProgramId}
                onChange={e => {
                  setSelectedProgramId(e.target.value);
                  setCustomWaMessage(null);
                }}
                className="w-full bg-surface-container-low rounded-xl px-4 py-2.5 text-xs font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 outline-none"
              >
                <option value="">Semua / Umum</option>
                {participant.enrolledPrograms.map(p => (
                  <option key={p.programId} value={p.programId}>
                    {p.name} ({p.code})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-1">
                <Bilingual {...copy.crm.labelTemplate} />
              </label>
              <select
                value={outreachType}
                onChange={e => {
                  setOutreachType(e.target.value as OutreachType);
                  setCustomWaMessage(null);
                }}
                className="w-full bg-surface-container-low rounded-xl px-4 py-2.5 text-xs font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 outline-none"
              >
                <option value="INACTIVITY_REMINDER">{t(copy.crm.templateInactivity)}</option>
                <option value="EXAM_REMINDER">{t(copy.crm.templateExam)}</option>
                <option value="GENERAL_FOLLOWUP">{t(copy.crm.templateGeneral)}</option>
              </select>
            </div>
          </div>

          {/* TAB 1: WHATSAPP */}
          {activeTab === 'WHATSAPP' && (
            <div className="space-y-4">
              {!participant.phone ? (
                <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center gap-3 text-xs text-amber-700 dark:text-amber-400 font-body">
                  <AlertCircle size={18} className="shrink-0" />
                  <span>
                    Peserta ini belum mencantumkan nomor telepon / WhatsApp di profilnya. Silakan
                    gunakan kanal Email atau minta peserta memperbarui profil.
                  </span>
                </div>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-1">
                      <Bilingual {...copy.crm.labelPreviewMessage} />
                    </label>
                    <textarea
                      rows={7}
                      value={waMessage}
                      onChange={e => setCustomWaMessage(e.target.value)}
                      className="w-full bg-surface-container-low rounded-2xl p-4 text-xs font-body text-on-surface border-none focus:ring-2 focus:ring-emerald-500/20 outline-none resize-none leading-relaxed"
                    />
                  </div>

                  {waSuccessNotice && (
                    <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-xs font-body flex items-center gap-2 animate-fadeIn">
                      <Check size={16} />
                      <span>Log outreach WhatsApp tercatat! Menghubungkan ke WhatsApp Web...</span>
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-3 pt-2">
                    <Button variant="ghost" onClick={onClose}>
                      Batal
                    </Button>
                    <Button
                      onClick={handleSendWhatsApp}
                      disabled={isSendingWa || !waMessage.trim()}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center gap-2 rounded-xl shadow-level1 px-5"
                    >
                      {isSendingWa ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <ExternalLink size={16} />
                      )}
                      <span>{t(copy.crm.btnOpenWhatsApp)}</span>
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* TAB 2: EMAIL */}
          {activeTab === 'EMAIL' && (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl bg-surface-container-low border border-outline-variant/10 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-sky-500/10 text-sky-600 flex items-center justify-center shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <h4 className="font-headline font-bold text-xs text-on-surface m-0">
                      Terkirim ke: {participant.email}
                    </h4>
                    <p className="text-[11px] text-on-surface-variant m-0 mt-0.5">
                      Pengingat otomatis menggunakan template resmi bertema Tarbiyah Sunnah Learning
                      via SMTP Server.
                    </p>
                  </div>
                </div>

                {outreachType === 'EXAM_REMINDER' && participant.missedEvaluations.length > 0 && (
                  <div className="space-y-1.5 pt-2 border-t border-outline-variant/10">
                    <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                      Pilih Evaluasi Tertunda
                    </label>
                    <select
                      value={selectedEvaluationId}
                      onChange={e => setSelectedEvaluationId(e.target.value)}
                      className="w-full bg-surface-container-lowest rounded-xl px-4 py-2.5 text-xs font-body text-on-surface border border-outline-variant/10 outline-none"
                    >
                      {participant.missedEvaluations.map(ev => (
                        <option key={ev.id} value={ev.id}>
                          {ev.title} ({ev.type}) - {ev.programName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {emailSentSuccess && (
                <div className="p-3.5 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-700 dark:text-sky-400 text-xs font-body flex items-center gap-2 animate-fadeIn">
                  <Check size={16} />
                  <span>
                    Email pengingat berhasil dikirim ke {participant.email} dan tercatat pada log
                    CRM!
                  </span>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button variant="ghost" onClick={onClose}>
                  Batal
                </Button>
                <Button
                  onClick={handleSendEmail}
                  disabled={isSendingEmail || emailSentSuccess}
                  className="bg-sky-600 hover:bg-sky-700 text-white font-bold flex items-center gap-2 rounded-xl shadow-level1 px-5"
                >
                  {isSendingEmail ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                  <span>{t(copy.crm.btnSendEmail)}</span>
                </Button>
              </div>
            </div>
          )}

          {/* TAB 3: MANUAL LOG / PHONE */}
          {activeTab === 'MANUAL' && (
            <form onSubmit={handleSaveManualLog} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-1">
                    <Bilingual {...copy.crm.labelChannel} />
                  </label>
                  <select
                    value={manualChannel}
                    onChange={e => setManualChannel(e.target.value as OutreachChannel)}
                    className="w-full bg-surface-container-low rounded-xl px-4 py-2.5 text-xs font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 outline-none"
                  >
                    <option value="PHONE">Telepon / Panggilan Suara</option>
                    <option value="WHATSAPP">WhatsApp Chat</option>
                    <option value="EMAIL">Email</option>
                    <option value="SYSTEM_NOTICE">Pesan / Notifikasi Sistem</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-1">
                    <Bilingual {...copy.crm.labelStatus} />
                  </label>
                  <select
                    value={manualStatus}
                    onChange={e => setManualStatus(e.target.value as OutreachStatus)}
                    className="w-full bg-surface-container-low rounded-xl px-4 py-2.5 text-xs font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 outline-none"
                  >
                    <option value="SENT">Terkirim (Menunggu Respons)</option>
                    <option value="REPLIED">Peserta Merespons / Ada Jawaban</option>
                    <option value="RESOLVED">Selesai / Masalah Teratasi (Kembali Aktif)</option>
                    <option value="NO_RESPONSE">Tidak Ada Respons / Tidak Aktif</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-1">
                  <Bilingual {...copy.crm.labelNotes} />
                </label>
                <textarea
                  rows={4}
                  value={manualNotes}
                  onChange={e => setManualNotes(e.target.value)}
                  placeholder={t(copy.crm.placeholderNotes)}
                  className="w-full bg-surface-container-low rounded-2xl p-4 text-xs font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 outline-none resize-none leading-relaxed"
                  required
                />
              </div>

              {manualSuccess && (
                <div className="p-3.5 rounded-xl bg-success/10 border border-success/20 text-success text-xs font-body flex items-center gap-2 animate-fadeIn">
                  <Check size={16} />
                  <span>Catatan interaksi berhasil disimpan ke riwayat CRM!</span>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button variant="ghost" onClick={onClose}>
                  Batal
                </Button>
                <Button
                  type="submit"
                  disabled={isCreatingLog || !manualNotes.trim() || manualSuccess}
                  className="bg-primary hover:opacity-90 text-white font-bold flex items-center gap-2 rounded-xl shadow-level1 px-5"
                >
                  {isCreatingLog ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Check size={16} />
                  )}
                  <span>{t(copy.crm.btnSaveLog)}</span>
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
